import axios from 'axios';

// Використовуємо динамічний baseURL:
// - у проді: '/api'
// - у деві: 'http://localhost:8000/api'
const API_BASE_URL =
  process.env.REACT_APP_API_URL ||
  (process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:8000/api');

// Токен сесії (видається /login/, див. backend production/auth_sessions.py)
export const AUTH_TOKEN_KEY = 'authToken';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

api.interceptors.request.use((config) => {
  try {
    // Якщо це FormData — не форсуємо application/json (axios сам поставить multipart з boundary)
    if (typeof FormData !== 'undefined' && config?.data instanceof FormData) {
      config.headers = config.headers || {};
      delete config.headers['Content-Type'];
      delete config.headers['content-type'];
    }
    // Сесія: бекенд визначає співробітника лише за токеном (X-Staff-Id від клієнта ігнорується)
    const token = localStorage.getItem(AUTH_TOKEN_KEY);
    if (token) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${token}`;
    }
  } catch {
    /* ignore */
  }
  return config;
});


/** Локальний вихід: прибрати токен і профіль (сесію на сервері завершує authService.logout). */
export function clearSession() {
  try {
    localStorage.removeItem(AUTH_TOKEN_KEY);
    localStorage.removeItem('currentUser');
  } catch {
    /* ignore */
  }
}

// Права змінились (роль/персональні) → бекенд віддає нову «версію прав» у заголовку кожної відповіді.
// Тоді тихо перечитуємо профіль: меню й доступи оновлюються без перелогіну (App слухає подію).
export const USER_UPDATED_EVENT = 'replast:user-updated';
let profileRefresh = null;

function checkPermsVersion(response) {
  const version = response?.headers?.['x-perms-version'];
  if (!version || profileRefresh) return;
  let stored = null;
  try {
    stored = JSON.parse(localStorage.getItem('currentUser') || 'null');
  } catch {
    /* ignore */
  }
  if (!stored || stored.perms_version === version) return;
  profileRefresh = api
    .get('/auth/me/')
    .then((res) => {
      const user = res.data;
      const permsChanged =
        JSON.stringify(stored.effective_permissions || {}) !== JSON.stringify(user.effective_permissions || {}) ||
        stored.role !== user.role;
      localStorage.setItem('currentUser', JSON.stringify(user));
      window.dispatchEvent(new CustomEvent(USER_UPDATED_EVENT, { detail: { user, permsChanged } }));
    })
    .catch(() => {})
    .finally(() => {
      profileRefresh = null;
    });
}

/** Не показувати HTML/traceback Django DEBUG як alert користувачу. */
api.interceptors.response.use(
  (response) => {
    checkPermsVersion(response);
    return response;
  },
  (error) => {
    checkPermsVersion(error?.response);
    // Сесія завершилась або недійсна → на сторінку входу
    if (error?.response?.status === 401 && error.response.data?.code === 'not_authenticated') {
      clearSession();
      if (window.location.pathname !== '/login') window.location.assign('/login');
    }
    try {
      const data = error?.response?.data;
      const looksHtml =
        typeof data === 'string' &&
        (/<!DOCTYPE/i.test(data) || /<html/i.test(data) || /FieldError|Traceback|Exception/i.test(data));
      if (looksHtml) {
        const msg = 'Не вдалося виконати запит. Спробуйте ще раз або зверніться до адміністратора.';
        error.response.data = { detail: msg };
        error.userMessage = msg;
      }
    } catch {
      /* ignore */
    }
    return Promise.reject(error);
  },
);

// Вироби
export const productService = {
  getAll: () => api.get('/products/'),
  getById: (id) => api.get(`/products/${id}/`),
  create: (data) => api.post('/products/', data),
  update: (id, data) => api.put(`/products/${id}/`, data),
  delete: (id) => api.delete(`/products/${id}/`),
  usage: (id) => api.get(`/products/${id}/usage/`),
  getAvailability: (id, fromDate) =>
    api.get(`/products/${id}/availability/`, {
      params: fromDate ? { from: fromDate } : {},
    }),
  /** Залишок Перемолу на складі сировини для виробу (для планера) */
  getPermolStock: (id) => api.get(`/products/${id}/permol-stock/`),
  uploadImage: (id, file) => {
    const formData = new FormData();
    formData.append('image', file);
    return api.post(`/products/${id}/upload-image/`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
};

// Виробничі замовлення
export const productionOrderService = {
  getAll: (params) => api.get('/production-orders/', { params: params || {} }),
  getById: (id) => api.get(`/production-orders/${id}/`),
  create: (data) => api.post('/production-orders/', data),
  update: (id, data) => api.put(`/production-orders/${id}/`, data),
  delete: (id) => api.delete(`/production-orders/${id}/`),
  start: (id, data) => api.post(`/production-orders/${id}/start/`, data || {}),
  requestSetup: (id, data) => api.post(`/production-orders/${id}/request-setup/`, data || {}),
  setup: (id, data) => api.post(`/production-orders/${id}/setup/`, data || {}),
  launch: (id, data) => api.post(`/production-orders/${id}/launch/`, data || {}),
  approveTempShip: (id, data) => api.post(`/production-orders/${id}/approve-temp-ship/`, data || {}),
  complete: (id, data) => api.post(`/production-orders/${id}/complete/`, data || {}),
  pause: (id, pause_reason, data) => api.post(`/production-orders/${id}/pause/`, { pause_reason, ...(data || {}) }),
  resume: (id, data) => api.post(`/production-orders/${id}/resume/`, data || {}),
  packagingRequests: () => api.get('/production-orders/packaging-requests/'),
  assignOperator: (id, operatorId) => api.post(`/production-orders/${id}/assign-operator/`, { operator_id: operatorId }),
  operatorResponse: (id, response, reason) => api.post(`/production-orders/${id}/operator-response/`, { response, reason: reason || '' }),
  unassignedOperatorCount: () => api.get('/production-orders/unassigned-operator-count/'),
  setAdditionalProcesses: (id, data) => api.post(`/production-orders/${id}/set-additional-processes/`, data),
  getMaterialPlan: (params) => api.get('/production-orders/material-plan/', { params }),
  getCoProductConfigs: (pressFormId, machineId, excludeProductId) => api.get('/production-orders/co-product-configs/', {
    params: {
      press_form: pressFormId,
      machine: machineId,
      ...(excludeProductId ? { exclude_product: excludeProductId } : {}),
    },
  }),
  updateCoProduced: (id, data) => api.post(`/production-orders/${id}/update-co-produced/`, data),
  shiftReportCandidates: (params) => api.get('/production-orders/shift-report-candidates/', { params: params || {} }),
};

export const setupRequestService = {
  getAll: (params) => api.get('/setup-requests/', { params: params || {} }),
  getById: (id) => api.get(`/setup-requests/${id}/`),
  create: (data) => api.post('/setup-requests/', data),
  take: (id, data) => api.post(`/setup-requests/${id}/take/`, data || {}),
  complete: (id, data) => api.post(`/setup-requests/${id}/complete/`, data || {}),
  redo: (data) => api.post('/setup-requests/redo/', data || {}),
};

/** Остання реальна вага/цикл з історії наладки для конфігу планування */
export const setupParameterHistoryService = {
  latest: (params) => api.get('/setup-parameter-history/latest/', { params: params || {} }),
};

export const operatorShiftReportService = {
  create: (data) => api.post('/operator-shift-reports/', data),
  /** Звіт + обовʼязкові показники лічильника ТПА: { reports, counters, low_reason?, low_comment? } */
  submitWithCounters: (data) => api.post('/operator-shift-reports/submit-with-counters/', data),
  previewEfficiency: (data) => api.post('/operator-shift-reports/preview-efficiency/', data),
  getAll: (params) => api.get('/operator-shift-reports/', { params: params || {} }),
};

export const efficiencyLowReasonService = {
  getAll: (params) => api.get('/efficiency-low-reasons/', { params: params || {} }),
  getAllAdmin: () => api.get('/efficiency-low-reasons/', { params: { all: 1 } }),
  create: (data) => api.post('/efficiency-low-reasons/', data),
  update: (id, data) => api.patch(`/efficiency-low-reasons/${id}/`, data),
};

export const operatorShiftEfficiencyService = {
  getAll: (params) => api.get('/operator-shift-efficiency/', { params: params || {} }),
  stats: (params) => api.get('/operator-shift-efficiency/stats/', { params: params || {} }),
};

// Черга сушіння (матеріали замовлення з needs_drying)
export const orderMaterialDryingService = {
  list: (params) => api.get('/order-material-drying/', { params: params || {} }),
  patch: (id, data) => api.patch(`/order-material-drying/${id}/`, data),
  start: (id) => api.post(`/order-material-drying/${id}/start/`),
  complete: (id) => api.post(`/order-material-drying/${id}/complete/`),
  cancel: (id) => api.post(`/order-material-drying/${id}/cancel/`),
};

// Лінія змішування сировини (задачі з планера)
export const mixingBatchService = {
  list: (params) => api.get('/mixing-batches/', { params: params || {} }),
  getById: (id) => api.get(`/mixing-batches/${id}/`),
  recordAdd: (id, data) => api.post(`/mixing-batches/${id}/record-add/`, data),
};

/** Контроль якості перед пакуванням (завершені замовлення з планера → «Склад для пакування»). */
export const prePackagingQCService = {
  list: (params) => api.get('/pre-packaging-qc/', { params: params || {} }),
  getById: (id) => api.get(`/pre-packaging-qc/${id}/`),
  addInspection: (id, data) => api.post(`/pre-packaging-qc/${id}/inspections/`, data),
  clearForPackaging: (id) => api.post(`/pre-packaging-qc/${id}/clear-for-packaging/`, {}),
};

export const blockedStockService = {
  getAll: (params) => api.get('/blocked-stock/', { params: params || {} }),
  getById: (id) => api.get(`/blocked-stock/${id}/`),
  create: (data) => api.post('/blocked-stock/', data),
  update: (id, data) => api.put(`/blocked-stock/${id}/`, data),
  resolve: (id, data) => api.post(`/blocked-stock/${id}/resolve/`, data),
  transfer: (id, data) => api.post(`/blocked-stock/${id}/transfer/`, data || {}),
  delete: (id) => api.delete(`/blocked-stock/${id}/`),
  previewFromQc: (params) => api.get('/blocked-stock/preview-from-qc/', { params: params || {} }),
  fromQc: (data) => api.post('/blocked-stock/from-qc/', data || {}),
  journal: (params) => api.get('/blocked-stock/journal/', { params: params || {} }),
  approveShip: (id, data) => api.post(`/blocked-stock/${id}/approve-ship/`, data || {}),
};

// Перерви на станках
export const machineBreakService = {
  getAll: () => api.get('/machine-breaks/'),
  create: (data) => api.post('/machine-breaks/', data),
  delete: (id) => api.delete(`/machine-breaks/${id}/`),
};

// Звіти виробництва
export const productionReportService = {
  getAll: () => api.get('/production-reports/'),
  create: (data) => api.post('/production-reports/', data),
};

// Прес-форми
export const pressFormService = {
  getAll: () => api.get('/press-forms/'),
  getById: (id) => api.get(`/press-forms/${id}/`),
  create: (data) => api.post('/press-forms/', data),
  update: (id, data) => api.put(`/press-forms/${id}/`, data),
  delete: (id) => api.delete(`/press-forms/${id}/`),
  getConfigs: (id) => api.get(`/press-forms/${id}/configs/`),
  saveConfigs: (id, data) => api.post(`/press-forms/${id}/save-configs/`, data),
};

// Станки
export const machineService = {
  getAll: () => api.get('/machines/'),
  getAllForSelect: () => api.get('/machines/all/'),
  getById: (id) => api.get(`/machines/${id}/`),
  create: (data) => api.post('/machines/', data),
  update: (id, data) => api.put(`/machines/${id}/`, data),
  delete: (id) => api.delete(`/machines/${id}/`),
  getAvailablePressForms: (id) => api.get(`/machines/${id}/available_press_forms/`),
};

// План обслуговування станків
export const maintenancePlanService = {
  getAll: () => api.get('/machine-maintenance/'),
  getById: (id) => api.get(`/machine-maintenance/${id}/`),
  create: (data) => api.post('/machine-maintenance/', data),
  update: (id, data) => api.put(`/machine-maintenance/${id}/`, data),
  delete: (id) => api.delete(`/machine-maintenance/${id}/`),
};

// Довідники сировини / барвників / упаковки
export const rawMaterialService = {
  getAll: () => api.get('/raw-materials/'),
  getById: (id) => api.get(`/raw-materials/${id}/`),
  create: (data) => api.post('/raw-materials/', data),
  /** Повне оновлення запису з форми (усі поля з форми). */
  update: (id, data) => api.put(`/raw-materials/${id}/`, data),
  /** Лише зміна частини полів (наприклад ціни в довіднику) — не затирає інші атрибути. */
  patch: (id, data) => api.patch(`/raw-materials/${id}/`, data),
  delete: (id) => api.delete(`/raw-materials/${id}/`),
  usage: (id) => api.get(`/raw-materials/${id}/usage/`),
};

export const dyeService = {
  getAll: () => api.get('/dyes/'),
  getById: (id) => api.get(`/dyes/${id}/`),
  create: (data) => api.post('/dyes/', data),
  update: (id, data) => api.put(`/dyes/${id}/`, data),
  patch: (id, data) => api.patch(`/dyes/${id}/`, data),
  delete: (id) => api.delete(`/dyes/${id}/`),
  usage: (id) => api.get(`/dyes/${id}/usage/`),
};

export const packagingMaterialService = {
  getAll: () => api.get('/packaging-materials/'),
  getById: (id) => api.get(`/packaging-materials/${id}/`),
  create: (data) => api.post('/packaging-materials/', data),
  update: (id, data) => api.put(`/packaging-materials/${id}/`, data),
  patch: (id, data) => api.patch(`/packaging-materials/${id}/`, data),
  delete: (id) => api.delete(`/packaging-materials/${id}/`),
  usage: (id) => api.get(`/packaging-materials/${id}/usage/`),
};

export const filamentService = {
  getAll: () => api.get('/filaments/'),
  getById: (id) => api.get(`/filaments/${id}/`),
  create: (data) => api.post('/filaments/', data),
  update: (id, data) => api.put(`/filaments/${id}/`, data),
  patch: (id, data) => api.patch(`/filaments/${id}/`, data),
  delete: (id) => api.delete(`/filaments/${id}/`),
  usage: (id) => api.get(`/filaments/${id}/usage/`),
};

export const consumableMaterialService = {
  getAll: () => api.get('/consumable-materials/'),
  getById: (id) => api.get(`/consumable-materials/${id}/`),
  create: (data) => api.post('/consumable-materials/', data),
  update: (id, data) => api.put(`/consumable-materials/${id}/`, data),
  patch: (id, data) => api.patch(`/consumable-materials/${id}/`, data),
  delete: (id) => api.delete(`/consumable-materials/${id}/`),
  usage: (id) => api.get(`/consumable-materials/${id}/usage/`),
};

export const maintenanceTicketService = {
  getAll: (params) => api.get('/maintenance-tickets/', { params: params || {} }),
  getById: (id) => api.get(`/maintenance-tickets/${id}/`),
  create: (data) => api.post('/maintenance-tickets/', data),
  patch: (id, data) => api.patch(`/maintenance-tickets/${id}/`, data),
  delete: (id) => api.delete(`/maintenance-tickets/${id}/`),
  addComment: (id, data) => api.post(`/maintenance-tickets/${id}/add-comment/`, data),
  forOperator: (machineIds) => api.get('/maintenance-tickets/for-operator/', { params: { machines: machineIds } }),
  markOperatorNotified: (id) => api.patch(`/maintenance-tickets/${id}/`, { operator_notified_at: new Date().toISOString() }),
};

export const maintenanceSettingsService = {
  get: () => api.get('/maintenance-settings/'),
  patch: (data) => api.patch('/maintenance-settings/1/', data),
};

export const checkMaintenanceAlerts = () => api.post('/check-maintenance-alerts/');
export const getMaintenanceDashboard = () => api.get('/maintenance-dashboard/');
export const getDowntimeReport = (params) => api.get('/downtime-report/', { params });

// Склади: залишки й операції
export const stockItemService = {
  getAll: (params) => api.get('/stock-items/', { params: params || {} }),
  getById: (id) => api.get(`/stock-items/${id}/`),
  create: (data) => api.post('/stock-items/', data),
  update: (id, data) => api.put(`/stock-items/${id}/`, data),
  delete: (id) => api.delete(`/stock-items/${id}/`),
};

export const stockTransactionService = {
  getAll: (params) => api.get('/stock-transactions/', { params: params || {} }),
  getById: (id) => api.get(`/stock-transactions/${id}/`),
  create: (data) => api.post('/stock-transactions/', data),
  update: (id, data) => api.put(`/stock-transactions/${id}/`, data),
  delete: (id) => api.delete(`/stock-transactions/${id}/`),
  /** Рекомендована ціна: закупівля → останній прихід → довідник */
  recommendedPrice: (params) => api.get('/stock-transactions/recommended-price/', { params: params || {} }),
};

/** Єдиний журнал рухів: звідки → куди */
export const inventoryMovementService = {
  getAll: (params) => api.get('/inventory-movements/', { params: params || {} }),
};

/** Активні склади системи (для дропдауна «Куди переміщуємо») */
export const warehouseService = {
  getAll: (params) => api.get('/warehouses/', { params: params || {} }),
};

// Надходження на склади (матеріали / напівфабрикати)
export const stockArrivalService = {
  getAll: (params) => api.get('/stock-arrivals/', { params: params || {} }),
  confirm: (id, data) => api.post(`/stock-arrivals/${id}/confirm/`, data || {}),
};

// Операції дроблення
export const crushingOperationService = {
  getAll: (params) => api.get('/crushing-operations/', { params: params || {} }),
  create: (data) => api.post('/crushing-operations/', data),
  confirm: (id, data) => api.post(`/crushing-operations/${id}/confirm/`, data || {}),
};

// Партії відходів / канбан дроблення / склад перемолів
export const crushingWasteLotService = {
  getAll: (params) => api.get('/crushing-waste-lots/', { params: params || {} }),
  startCrushing: (id, data) => api.post(`/crushing-waste-lots/${id}/start-crushing/`, data || {}),
  completeCrushing: (id, data) => api.post(`/crushing-waste-lots/${id}/complete-crushing/`, data || {}),
  /** Ручне переміщення перемолу на інший склад (production / raw / technical). */
  transferOut: (data) => api.post('/crushing-waste-lots/transfer-out/', data || {}),
};

// Склад взірців: залишки та переміщення з ГП / з взірців на інший склад
export const sampleWarehouseService = {
  getStock: (params) => api.get('/sample-warehouse/stock/', { params: params || {} }),
  transfer: (data) => api.post('/sample-warehouse/transfer/', data || {}),
  transferOut: (data) => api.post('/sample-warehouse/transfer-out/', data || {}),
  getTransfers: (params) => api.get('/sample-warehouse/transfers/', { params: params || {} }),
};

// Склад браку
export const defectStockService = {
  getAll: (params) => api.get('/production-defect-stock/', { params: params || {} }),
  sendToWaste: (id, data) => api.post(`/production-defect-stock/${id}/send-to-waste/`, data || {}),
  transfer: (id, data) => api.post(`/production-defect-stock/${id}/transfer/`, data || {}),
};

// Упаковка (напівфабрикати → склад призначення)
export const packagingTaskService = {
  semiStockItems: () => api.get('/packaging-tasks/semi-stock-items/'),
  list: (params) => api.get('/packaging-tasks/', { params: params || {} }),
  patch: (id, data) => api.patch(`/packaging-tasks/${id}/`, data),
  start: (data) => api.post('/packaging-tasks/start/', data),
  complete: (id, data) => api.post(`/packaging-tasks/${id}/complete/`, data || {}),
  cancel: (id) => api.post(`/packaging-tasks/${id}/cancel/`, {}),
};

export const packerShiftOutputService = {
  getAll: (params) => api.get('/packer-shift-outputs/', { params: params || {} }),
  stats: (params) => api.get('/packer-shift-outputs/stats/', { params: params || {} }),
};

// Норма пакування на день (менеджер упаковки)
export const packagingDailyNormService = {
  getAll: (params) => api.get('/packaging-daily-norms/', { params: params || {} }),
  create: (data) => api.post('/packaging-daily-norms/', data),
  update: (id, data) => api.put(`/packaging-daily-norms/${id}/`, data),
  delete: (id) => api.delete(`/packaging-daily-norms/${id}/`),
};

// Готова продукція: прихід / відвантаження / залишки
export const finishedGoodsReceiptService = {
  getAll: (params) => api.get('/finished-goods-receipts/', { params: params || {} }),
  create: (data) => api.post('/finished-goods-receipts/', data),
  /** Ручний прихід у вільний залишок (без замовлення). Право: menu.stock.finished.manual_receipt */
  manualIn: (data) => api.post('/finished-goods-receipts/manual-in/', data || {}),
  /** Передача зі складу виробництва → «Надходження» СГП (без зарахування в залишки). */
  releaseToFinished: (id, data) =>
    api.post(`/finished-goods-receipts/${id}/release-to-finished/`, data || {}),
  /** Підтвердження комірником у «Надходженнях» → залишки + журнал. */
  confirm: (id, data) => api.post(`/finished-goods-receipts/${id}/confirm/`, data || {}),
};

export const finishedGoodsShipmentService = {
  getAll: (params) => api.get('/finished-goods-shipments/', { params: params || {} }),
  create: (data) => api.post('/finished-goods-shipments/', data),
  /** Атомарне мульти-відвантаження (один shipment_group_id) */
  bulkCreate: (lines) => api.post('/finished-goods-shipments/bulk-create/', { lines }),
};

export const finishedGoodsBalancesService = {
  getBalances: () => api.get('/finished-goods-balances/'),
  /** Ручне переміщення ГП на будь-який склад. */
  transferOut: (data) => api.post('/finished-goods/transfer-out/', data || {}),
};

// Закупівлі (purchase orders)
export const purchaseOrderService = {
  getAll: (params) => api.get('/purchase-orders/', { params: params || {} }),
  getById: (id) => api.get(`/purchase-orders/${id}/`),
  create: (data) => api.post('/purchase-orders/', data),
  update: (id, data) => api.put(`/purchase-orders/${id}/`, data),
  patch: (id, data) => api.patch(`/purchase-orders/${id}/`, data),
  delete: (id) => api.delete(`/purchase-orders/${id}/`),
  pendingApprovalCount: () => api.get('/purchase-orders/pending-approval-count/'),
  /** Після відхилення: знову в чергу погодження (лише для ролі з menu.purchases). */
  requestReapproval: (id) => api.post(`/purchase-orders/${id}/request-reapproval/`, {}),
};

// Довідник замовників (контрагенти + прайс по виробах)
export const customerDirectoryService = {
  getAll: (params) => api.get('/customers/', { params: params || {} }),
  getById: (id) => api.get(`/customers/${id}/`),
  create: (data) => api.post('/customers/', data),
  update: (id, data) => api.put(`/customers/${id}/`, data),
  delete: (id) => api.delete(`/customers/${id}/`),
};

// Довідник постачальників (для закупівель)
export const supplierDirectoryService = {
  getAll: (params) => api.get('/suppliers/', { params: params || {} }),
  getById: (id) => api.get(`/suppliers/${id}/`),
  create: (data) => api.post('/suppliers/', data),
  update: (id, data) => api.put(`/suppliers/${id}/`, data),
  patch: (id, data) => api.patch(`/suppliers/${id}/`, data),
  delete: (id) => api.delete(`/suppliers/${id}/`),
};

// Замовлення клієнтів
export const customerOrderService = {
  getAll: (params) => api.get('/customer-orders/', { params: params || {} }),
  getById: (id) => api.get(`/customer-orders/${id}/`),
  create: (data) => api.post('/customer-orders/', data),
  update: (id, data) => api.put(`/customer-orders/${id}/`, data),
  delete: (id) => api.delete(`/customer-orders/${id}/`),
  nextNumber: (orderDateIso) =>
    api.get('/customer-orders/next-number/', { params: { order_date: orderDateIso } }),
  /** Перевірка нестачі сировини; опційно створює/оновлює заявку на закупівлю для збереженого замовлення. */
  materialShortagePreview: (lines, opts = {}) =>
    api.post('/customer-orders/material-shortage-preview/', {
      lines,
      persist_purchase_request: Boolean(opts.persistPurchaseRequest),
      customer_order_id: opts.customerOrderId ?? null,
    }),
};

// Звітні періоди для нумерації клієнтських замовлень
export const orderReportingPeriodService = {
  getAll: () => api.get('/order-reporting-periods/'),
  create: (data) => api.post('/order-reporting-periods/', data),
  update: (id, data) => api.put(`/order-reporting-periods/${id}/`, data),
  patch: (id, data) => api.patch(`/order-reporting-periods/${id}/`, data),
  delete: (id) => api.delete(`/order-reporting-periods/${id}/`),
};

export const purchaseRequestService = {
  getAll: () => api.get('/purchase-requests/'),
  getById: (id) => api.get(`/purchase-requests/${id}/`),
  patch: (id, data) => api.patch(`/purchase-requests/${id}/`, data),
};

// Ролі та персонал
export const roleService = {
  getAll: () => api.get('/roles/'),
  create: (data) => api.post('/roles/', data),
  update: (id, data) => api.put(`/roles/${id}/`, data),
  delete: (id) => api.delete(`/roles/${id}/`),
};

export const staffService = {
  getAll: (params) => api.get('/staff/', { params: params || {} }),
  getById: (id) => api.get(`/staff/${id}/`),
  create: (data) => api.post('/staff/', data),
  update: (id, data) => api.put(`/staff/${id}/`, data),
  patch: (id, data) => api.patch(`/staff/${id}/`, data),
  delete: (id) => api.delete(`/staff/${id}/`),
};

export const workShiftService = {
  getAll: (params) => api.get('/work-shifts/', { params: params || {} }),
  create: (data) => api.post('/work-shifts/', data),
  update: (id, data) => api.put(`/work-shifts/${id}/`, data),
  patch: (id, data) => api.patch(`/work-shifts/${id}/`, data),
  delete: (id) => api.delete(`/work-shifts/${id}/`),
};

export const dailyMachineOperatorService = {
  getAll: (params) => api.get('/daily-machine-operators/', { params: params || {} }),
  bulkSave: (data) => api.post('/daily-machine-operators/bulk-save/', data),
};

// Месенджер між співробітниками (X-Staff-Id додає інтерсептор)
export const messengerService = {
  listConversations: () => api.get('/messenger/conversations/'),
  unreadCount: () => api.get('/messenger/conversations/unread-count/'),
  createConversation: (data) => api.post('/messenger/conversations/', data),
  updateConversation: (id, data) => api.patch(`/messenger/conversations/${id}/`, data),
  deleteConversation: (id) => api.delete(`/messenger/conversations/${id}/`),
  listMessages: (id) => api.get(`/messenger/conversations/${id}/messages/`),
  sendMessage: (id, body) => api.post(`/messenger/conversations/${id}/send/`, { body }),
  markRead: (id) => api.post(`/messenger/conversations/${id}/mark-read/`, {}),
};

/** «Задачі» — Synvolve Tasks (окремий сервіс tasks/, iframe) */
export const tasksAppService = {
  // Одноразове посилання входу для поточного співробітника
  login: () => api.post('/tasks-app/login/', {}),
  // Непрочитані сповіщення — бейдж біля «Задачі» в меню
  badge: () => api.get('/tasks-app/badge/'),
};

/** LMS: курси, тести, матеріали, версії */
export const lmsService = {
  courses: (params) => api.get('/lms/courses/', { params: params || {} }),
  course: (id) => api.get(`/lms/courses/${id}/`),
  courseCreate: (data) => api.post('/lms/courses/', data),
  courseUpdate: (id, data) => api.put(`/lms/courses/${id}/`, data),
  coursePatch: (id, data) => api.patch(`/lms/courses/${id}/`, data),
  courseDelete: (id) => api.delete(`/lms/courses/${id}/`),
  coursePublish: (id) => api.post(`/lms/courses/${id}/publish/`),
  courseUnpublish: (id) => api.post(`/lms/courses/${id}/unpublish/`),
  courseOpen: (id) => api.post(`/lms/courses/${id}/open/`),
  courseComplete: (id) => api.post(`/lms/courses/${id}/complete/`),
  courseProgress: (id) => api.get(`/lms/courses/${id}/progress/`),
  groups: () => api.get('/lms/course-groups/'),
  groupCreate: (data) => api.post('/lms/course-groups/', data),
  groupUpdate: (id, data) => api.put(`/lms/course-groups/${id}/`, data),
  groupDelete: (id) => api.delete(`/lms/course-groups/${id}/`),
  quizzes: (params) => api.get('/lms/quizzes/', { params: params || {} }),
  quiz: (id) => api.get(`/lms/quizzes/${id}/`),
  quizCreate: (data) => api.post('/lms/quizzes/', data),
  quizUpdate: (id, data) => api.put(`/lms/quizzes/${id}/`, data),
  quizDelete: (id) => api.delete(`/lms/quizzes/${id}/`),
  quizPublish: (id) => api.post(`/lms/quizzes/${id}/publish/`),
  quizUnpublish: (id) => api.post(`/lms/quizzes/${id}/unpublish/`),
  quizSubmit: (id, data) => api.post(`/lms/quizzes/${id}/submit/`, data),
  quizGrants: (id) => api.get(`/lms/quizzes/${id}/grants/`),
  quizGrant: (id, staffId) => api.post(`/lms/quizzes/${id}/grants/`, { staff_id: staffId }),
  quizGrantRevoke: (id, staffId) => api.delete(`/lms/quizzes/${id}/grants/${staffId}/`),
  attempts: () => api.get('/lms/quiz-attempts/'),
  attempt: (id) => api.get(`/lms/quiz-attempts/${id}/`),
  materials: (params) => api.get('/lms/materials/', { params: params || {} }),
  materialCreate: (data) => api.post('/lms/materials/', data),
  materialUpdate: (id, data) => api.put(`/lms/materials/${id}/`, data),
  materialDelete: (id) => api.delete(`/lms/materials/${id}/`),
  /** Завантаження файлів у базу знань (txt/md/pdf/docx/…) → матеріал + RAG */
  materialUploadKb: (files, meta = {}, config) => {
    const fd = new FormData();
    const list = Array.isArray(files) ? files : [files];
    list.forEach((f) => { if (f) fd.append('files', f); });
    if (meta.source) fd.append('source', meta.source);
    if (meta.base) fd.append('base', meta.base);
    if (meta.module) fd.append('module', meta.module);
    return api.post('/lms/materials/upload-kb/', fd, config || {});
  },
  versions: (entityType, entityId) => api.get('/lms/versions/', { params: { entity_type: entityType, entity_id: entityId } }),
  versionRestore: (id) => api.post(`/lms/versions/${id}/restore/`),
  dashboard: () => api.get('/lms/dashboard/'),
  aiStatus: () => api.get('/lms/ai/status/'),
  aiRewrite: (data) => api.post('/lms/ai/rewrite/', data),
  aiGenerateQuiz: (data) => api.post('/lms/ai/generate-quiz/', data),
  aiGradeOpen: (data) => api.post('/lms/ai/grade-open/', data),
  ragSearch: (q, k = 8) => api.get('/lms/rag/search/', { params: { q, k } }),
  ragVectorize: (data) => api.post('/lms/rag/vectorize/', data || {}),
  seSettings: () => api.get('/lms/smartexpert/settings/'),
  seSettingsSave: (data) => api.post('/lms/smartexpert/settings/', data),
  seStatus: () => api.get('/lms/smartexpert/status/'),
  seSync: (data) => api.post('/lms/smartexpert/sync/', data || {}),
  upload: (file, config) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post('/lms/upload/', fd, config || {});
  },
  uploadUrl: (url) => api.post('/lms/upload-url/', { url }),
};

// Логін
export const authService = {
  /** Вхід: зберігає токен сесії окремо від профілю; повертає профіль без токена. */
  login: async (data) => {
    const res = await api.post('/login/', data);
    const { token, ...user } = res.data;
    localStorage.setItem(AUTH_TOKEN_KEY, token);
    localStorage.setItem('currentUser', JSON.stringify(user));
    return user;
  },
  /** Завершує сесію на сервері й локально (помилки мережі не заважають виходу). */
  logout: () => api.post('/logout/', {}).catch(() => {}).finally(clearSession),
};

// Універсальний експорт (xlsx)
export const exportService = {
  entities: () => api.get('/exports/entities/'),
  /** @param {string|string[]} entityKey — одна сутність або масив (кілька аркушів) */
  runXlsx: (entityKey, params) => {
    const keys = Array.isArray(entityKey) ? entityKey.filter(Boolean) : [entityKey].filter(Boolean);
    const query = { ...(params || {}) };
    if (keys.length > 1) {
      query.entities = keys.join(',');
    } else if (keys.length === 1) {
      query.entity = keys[0];
    }
    return api.get('/exports/run/', {
      params: query,
      responseType: 'blob',
    });
  },
};

// Універсальний імпорт (xlsx)
export const importService = {
  entities: () => api.get('/imports/entities/'),
  preview: (entityKey, file) => {
    const fd = new FormData();
    fd.append('entity', entityKey);
    fd.append('file', file);
    // ВАЖЛИВО: не задаємо Content-Type вручну, щоб axios додав boundary
    return api.post('/imports/preview/', fd);
  },
  run: (entityKey, file, mapping) => {
    const fd = new FormData();
    fd.append('entity', entityKey);
    fd.append('file', file);
    fd.append('mapping', JSON.stringify(mapping || {}));
    // ВАЖЛИВО: не задаємо Content-Type вручну, щоб axios додав boundary
    return api.post('/imports/run/', fd);
  },
};

// Головна: операційний зріз виробництва
export const dashboardService = {
  productionSnapshot: (params) =>
    api.get('/dashboard/production-snapshot/', { params: params || {} }),
  /** Випуск і план vs факт (рядки звітів виробництва) */
  productionOutputSnapshot: (params) =>
    api.get('/dashboard/production-output-snapshot/', { params: params || {} }),
  /** Матеріали та внутрішня логістика */
  materialsLogisticsSnapshot: (params) =>
    api.get('/dashboard/materials-logistics-snapshot/', { params: params || {} }),
  /** Готова продукція та відвантаження */
  finishedGoodsSnapshot: (params) =>
    api.get('/dashboard/finished-goods-snapshot/', { params: params || {} }),
  /** Дашборд начальника зміни: ТПА live + звіти */
  shiftFloor: (params) =>
    api.get('/dashboard/shift-floor/', { params: params || {} }),
  /** Деталі / історія по ТПА */
  shiftFloorMachine: (id, params) =>
    api.get(`/dashboard/shift-floor/machines/${id}/`, { params: params || {} }),
  /** Масове планування перерви на вибрані ТПА */
  scheduleBreaks: (data) =>
    api.post('/dashboard/shift-floor/schedule-breaks/', data),
  /** Достроково завершити / скасувати перерву */
  endBreak: (breakId, data) =>
    api.post(`/dashboard/shift-floor/end-break/${breakId}/`, data || {}),
};

// Налаштування контролю якості
export const machineQualitySettingsService = {
  getAll: () => api.get('/machine-quality-settings/'),
  getById: (id) => api.get(`/machine-quality-settings/${id}/`),
  upsert: (data) => {
    if (data.id) {
      return api.put(`/machine-quality-settings/${data.id}/`, data);
    }
    return api.post('/machine-quality-settings/', data);
  },
  delete: (id) => api.delete(`/machine-quality-settings/${id}/`),
};

// Довідник причин браку (ВКЯ)
export const defectReasonService = {
  getAll: (params) => api.get('/defect-reasons/', { params: params || {} }),
  getAllAdmin: () => api.get('/defect-reasons/', { params: { all: 1 } }),
  create: (data) => api.post('/defect-reasons/', data),
  update: (id, data) => api.patch(`/defect-reasons/${id}/`, data),
  delete: (id) => api.delete(`/defect-reasons/${id}/`),
};

// Події контролю якості
export const qualityCheckService = {
  getAll: (params) => api.get('/quality-checks/', { params }),
  getPending: (params) => api.get('/quality-checks/pending/', { params }),
  trigger: (data) => api.post('/quality-checks/trigger/', data),
  complete: (id, data) => {
    // Не задаємо Content-Type вручну — axios сам додасть boundary для FormData.
    if (typeof FormData !== 'undefined' && data instanceof FormData) {
      return api.post(`/quality-checks/${id}/complete/`, data);
    }
    return api.post(`/quality-checks/${id}/complete/`, data);
  },
};

// Нотифікації / системний лог
export const notificationService = {
  getAll: (params) => api.get('/notifications/', { params: params || {} }),
  markRead: (ids) => api.post('/notifications/mark-read/', { ids }),
  markAllRead: () => api.post('/notifications/mark-all-read/', {}),
};

/** Дублювання системних сповіщень у Telegram (прив'язка через бота). */
export const crmService = {
  // Одноразове посилання автологіну в Twenty для поточного співробітника
  twentyLogin: () => api.post('/crm/twenty-login/', {}),
};

export const learningService = {
  // Одноразове посилання входу в «Навчання NEW» для поточного співробітника
  login: (next = '/courses') => api.post('/learning/login/', { next }),
};

export const telegramService = {
  status: () => api.get('/telegram/status/'),
  linkRequest: () => api.post('/telegram/link-request/', {}),
  unlink: () => api.post('/telegram/unlink/', {}),
};

// Web Push (PWA) — заявки на закупівлю тощо
export const pushService = {
  getVapidPublicKey: () => api.get('/push/vapid-public-key/'),
  subscribe: (data) => api.post('/push/subscribe/', data),
  unsubscribe: (data) => api.post('/push/unsubscribe/', data),
};

export const orderQualityCheckService = {
  getAll: (params) => api.get('/order-quality-checks/', { params: params || {} }),
  getById: (id) => api.get(`/order-quality-checks/${id}/`),
  create: (data) => api.post('/order-quality-checks/', data),
  requestRecheck: (id, data) => api.post(`/order-quality-checks/${id}/request-recheck/`, data || {}),
  complete: (id, data) => {
    // Не задаємо Content-Type вручну — axios сам додасть boundary для FormData.
    if (typeof FormData !== 'undefined' && data instanceof FormData) {
      return api.post(`/order-quality-checks/${id}/complete/`, data);
    }
    return api.post(`/order-quality-checks/${id}/complete/`, data || {});
  },
};

export const qcStockService = {
  getAll: (params) => api.get('/qc-stock/', { params: params || {} }),
  resolve: (id, data) => api.post(`/qc-stock/${id}/resolve/`, data || {}),
};

export const additionalProcessService = {
  getAll: (params) => api.get('/additional-processes/', { params: params || {} }),
  create: (data) => api.post('/additional-processes/', data),
  update: (id, data) => api.patch(`/additional-processes/${id}/`, data),
  remove: (id) => api.delete(`/additional-processes/${id}/`),
};

export const orderAdditionalProcessService = {
  getAll: (params) => api.get('/order-additional-processes/', { params: params || {} }),
  setOperatorStatus: (id, data) => api.post(`/order-additional-processes/${id}/set-operator-status/`, data),
  report: (id, data) => api.post(`/order-additional-processes/${id}/report/`, data),
};

export const mixingBatchStockService = {
  getAll: (params) => api.get('/mixing-batch-stock/', { params: params || {} }),
  getById: (id) => api.get(`/mixing-batch-stock/${id}/`),
  create: (data) => api.post('/mixing-batch-stock/', data),
  update: (id, data) => api.patch(`/mixing-batch-stock/${id}/`, data),
};

export const productKitService = {
  getAll: (params) => api.get('/product-kits/', { params: params || {} }),
  getById: (id) => api.get(`/product-kits/${id}/`),
  create: (data) => api.post('/product-kits/', data),
  update: (id, data) => api.patch(`/product-kits/${id}/`, data),
  remove: (id) => api.delete(`/product-kits/${id}/`),
};

export const leadService = {
  getAll: (params) => api.get('/leads/', { params: params || {} }),
  getById: (id) => api.get(`/leads/${id}/`),
  create: (data) => api.post('/leads/', data),
  update: (id, data) => api.patch(`/leads/${id}/`, data),
  remove: (id) => api.delete(`/leads/${id}/`),
  addComment: (id, data) => api.post(`/leads/${id}/add-comment/`, data),
  convertToCustomer: (id) => api.post(`/leads/${id}/convert-to-customer/`),
};

export const projectService = {
  getAll: (params) => api.get('/projects/', { params: params || {} }),
  getById: (id) => api.get(`/projects/${id}/`),
  create: (data) => api.post('/projects/', data),
  update: (id, data) => api.patch(`/projects/${id}/`, data),
  remove: (id) => api.delete(`/projects/${id}/`),
  updateModel3D: (id, data) => api.patch(`/projects/${id}/model-3d/`, data),
  updatePressFormProcess: (id, data) => api.patch(`/projects/${id}/press-form-process/`, data),
  addPayment: (id, data) => api.post(`/projects/${id}/add-payment/`, data),
  deletePayment: (id, paymentId) => api.delete(`/projects/${id}/payments/${paymentId}/`),
};

export const activityLogService = {
  getAll: (params) => api.get('/activity-log/', { params }),
  getActionChoices: () => api.get('/activity-log/action_choices/'),
  log: (data) => api.post('/activity-log/log/', data),
};

export const notificationRuleService = {
  getAll: () => api.get('/notification-rules/'),
  create: (data) => api.post('/notification-rules/', data),
  update: (id, data) => api.patch(`/notification-rules/${id}/`, data),
  remove: (id) => api.delete(`/notification-rules/${id}/`),
  getActionChoices: () => api.get('/notification-rules/action_choices/'),
};

export const rndService = {
  // Boards
  getBoards: () => api.get('/rnd-boards/'),
  getBoard: (id) => api.get(`/rnd-boards/${id}/`),
  createBoard: (data) => api.post('/rnd-boards/', data),
  updateBoard: (id, data) => api.patch(`/rnd-boards/${id}/`, data),
  deleteBoard: (id) => api.delete(`/rnd-boards/${id}/`),
  reorderGroups: (boardId, order) => api.post(`/rnd-boards/${boardId}/reorder-groups/`, { order }),
  createDefaultStatuses: (boardId) => api.post(`/rnd-boards/${boardId}/create-default-statuses/`),
  // Statuses
  getStatuses: (boardId) => api.get('/rnd-statuses/', { params: { board: boardId } }),
  createStatus: (data) => api.post('/rnd-statuses/', data),
  updateStatus: (id, data) => api.patch(`/rnd-statuses/${id}/`, data),
  deleteStatus: (id) => api.delete(`/rnd-statuses/${id}/`),
  // Groups
  getGroups: (boardId) => api.get('/rnd-groups/', { params: { board: boardId } }),
  createGroup: (data) => api.post('/rnd-groups/', data),
  updateGroup: (id, data) => api.patch(`/rnd-groups/${id}/`, data),
  deleteGroup: (id) => api.delete(`/rnd-groups/${id}/`),
  // Tasks
  getTasks: (params) => api.get('/rnd-tasks/', { params }),
  getTask: (id) => api.get(`/rnd-tasks/${id}/`),
  createTask: (data) => api.post('/rnd-tasks/', data),
  updateTask: (id, data) => api.patch(`/rnd-tasks/${id}/`, data),
  moveTask: (id, data) => api.patch(`/rnd-tasks/${id}/move/`, data),
  deleteTask: (id) => api.delete(`/rnd-tasks/${id}/`),
  reorderTasks: (order) => api.post('/rnd-tasks/reorder/', { order }),
  // Subtasks
  getSubtask: (id) => api.get(`/rnd-subtasks/${id}/`),
  createSubtask: (data) => api.post('/rnd-subtasks/', data),
  updateSubtask: (id, data) => api.patch(`/rnd-subtasks/${id}/`, data),
  deleteSubtask: (id) => api.delete(`/rnd-subtasks/${id}/`),
  // Comments
  createComment: (data) => api.post('/rnd-comments/', data),
  updateComment: (id, data) => api.patch(`/rnd-comments/${id}/`, data),
  deleteComment: (id) => api.delete(`/rnd-comments/${id}/`),
  // Contractors
  getContractors: (params) => api.get('/rnd-contractors/', { params }),
  getContractor: (id) => api.get(`/rnd-contractors/${id}/`),
  createContractor: (data) => api.post('/rnd-contractors/', data),
  updateContractor: (id, data) => api.patch(`/rnd-contractors/${id}/`, data),
  deleteContractor: (id) => api.delete(`/rnd-contractors/${id}/`),
};

export const unifiedStockService = {
  getAll: (params) => api.get('/unified-stock/', { params }),
};

export default api;
